import { useRef, useState } from "react";
import { toast } from "sonner";
import { captureException } from "@/lib/monitoring";
import { trackEvent } from "@/lib/analytics";
import { fileService } from "@/lib/services/fileService";
import type { ReviewFile } from "@/lib/db/models";
import type { ReportMetadata } from "@/lib/report/metadata";
import { shortVersionString } from "@/lib/report/metadata";
import { CHAPTERS, getChapterByInternalId } from "@/lib/report/chapter-registry";
import {
  PACKAGE_IDENTITY,
  getPackageIdentity,
  type ReportPackage,
} from "@/lib/report/package-identity";

import {
  buildChapterOnlyPdfFilenameBase,
  buildSectionPdfFilenameBase,
} from "@/lib/report/filenames";
import { setScopedExportActive } from "@/lib/report/export-mode";


export type { ReportPackage } from "@/lib/report/package-identity";
export { PACKAGE_IDENTITY, getPackageIdentity } from "@/lib/report/package-identity";

/* ─────────────────────── PDF tree mount registry ─────────────────────── */

/**
 * Handle returned by a registered PDF tree host. `root` is a live DOM node
 * containing the freshly-mounted PDF publication tree; `release()` unmounts
 * it so no third copy of the report lingers after export.
 */
export interface PdfTreeHandle {
  root: HTMLElement;
  release: () => void;
}
export type PdfTreeMountFn = (pkg: ReportPackage) => Promise<PdfTreeHandle>;

let pdfTreeMountFn: PdfTreeMountFn | null = null;
/**
 * Register (or clear) an on-demand PDF-tree mount function. The report page
 * calls this on mount so `generatePdfFromDom` can materialize the PDF
 * publication tree lazily — only for the duration of an export.
 */
export function registerPdfTreeMount(fn: PdfTreeMountFn | null) {
  pdfTreeMountFn = fn;
}

/**
 * Warm the PDF-engine bundles during idle time so the first click on
 * "Download Report" doesn't pay the dynamic-import cost. Safe to call
 * repeatedly; the browser dedupes cached module fetches.
 */
export function preloadPdfLibraries() {
  void import("html2canvas-pro").catch(() => undefined);
  void import("jspdf").catch(() => undefined);
  void import("pdf-lib").catch(() => undefined);
}

/* ─────────────────────── perf instrumentation ─────────────────────── */

interface PerfStage {
  name: string;
  ms: number;
}
interface PerfTimeline {
  start: (name: string) => void;
  end: (name: string) => number;
  entries: () => PerfStage[];
}
function createPerfTimeline(): PerfTimeline {
  const stages: PerfStage[] = [];
  const active = new Map<string, number>();
  const hasPerf = typeof performance !== "undefined";
  const safeMark = (n: string) => {
    if (!hasPerf) return;
    try {
      performance.mark(n);
    } catch {
      /* ignore */
    }
  };
  const safeMeasure = (name: string, s: string, e: string) => {
    if (!hasPerf) return;
    try {
      performance.measure(name, s, e);
    } catch {
      /* ignore */
    }
  };
  return {
    start(name) {
      active.set(name, hasPerf ? performance.now() : Date.now());
      safeMark(`pdf:${name}:start`);
    },
    end(name) {
      const t0 = active.get(name);
      if (t0 === undefined) return 0;
      active.delete(name);
      const now = hasPerf ? performance.now() : Date.now();
      const dur = now - t0;
      stages.push({ name, ms: dur });
      safeMark(`pdf:${name}:end`);
      safeMeasure(`pdf:${name}`, `pdf:${name}:start`, `pdf:${name}:end`);
      return dur;
    },
    entries: () => stages.slice(),
  };
}

function readAmbientReportMetadata(): ReportMetadata | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { __CLAIMGUARD_REPORT_METADATA__?: ReportMetadata };
  const m = w.__CLAIMGUARD_REPORT_METADATA__;
  if (!m || typeof m !== "object" || !m.report_uuid) return null;
  return m;
}

/**
 * Claim Intelligence Package™ — PDF Engine V2
 *
 * V1 rendered the entire report as a single html2canvas bitmap and sliced
 * it into pages. That produced overlapping cards, ghosted evidence chains,
 * clipped tables and enormous PDFs.
 *
 * V2 assembles the PDF **page by page**:
 *
 *  1.  Walks the report DOM and collects atomic "blocks" (report cover,
 *      package-contents card, and every top-level `<section>` /
 *      `.print-avoid-break` container). Each block is treated as an atom
 *      that cannot be split across pages.
 *  2.  Blocks larger than a single printable page are decomposed into
 *      their children so we always flow with pieces that fit.
 *  3.  Each block is rasterized **individually** with html2canvas (small
 *      per-block bitmaps — never one giant capture) and placed onto jsPDF
 *      pages. When a block does not fit on the current page, a new page
 *      is started. There is no slicing across pages, so no ghosting and
 *      no duplicated content.
 *  4.  Consistent header / footer chrome is drawn programmatically on
 *      every page.
 *  5.  Original invoice PDF pages are appended as Appendix A via pdf-lib;
 *      original invoice images are rendered onto full PDF pages so the
 *      Appendix always contains the real document, never a placeholder.
 */

type PageSize = "a4" | "letter";

interface Options {
  pageSize?: PageSize;
  invoiceNumber?: string;
  fileNumber?: string;
  invoiceFiles?: ReviewFile[];
  /** Override the export root (used by per-section export). */
  rootElement?: HTMLElement | null;
  /** Skip Appendix A / invoice attachments — used by per-section export. */
  skipInvoiceAppendix?: boolean;
  /** Override final download filename (without .pdf). */
  filenameBase?: string;
  /** Optional section title shown in the header sub-line. */
  sectionTitle?: string;
  /**
   * Restrict which chapter blocks are included in the export. When set, any
   * top-level block whose id begins with `chapter-` (or equals `appendix-a`)
   * is kept only if its id is present in this list. Non-chapter shell blocks
   * (cover page, TOC, closing) are always included regardless.
   */
  includeAnchors?: string[];
  /** Selected download package (drives cover/TOC/footer/metadata/filename identity). */
  packageType?: ReportPackage;
  /**
   * Optional progress callback invoked between block renders.
   * Enables the caller to update a toast so the button never appears frozen.
   */
  onProgress?: (info: {
    phase: "preparing" | "rendering" | "assembling" | "finalizing";
    rendered: number;
    total: number;
  }) => void;
  /** Cancels the export at the next safe boundary (no file is produced). */
  signal?: AbortSignal;
}


const MARGIN_MM = 12;
const HEADER_H_MM = 12;
const FOOTER_H_MM = 14;
const GAP_MM = 6;
/**
 * Rasterization quality. html2canvas produces bitmaps; the report is
 * embedded as PNG (lossless — keeps typography, table rules, chart strokes,
 * and icon edges crisp at 100 / 150 / 200% zoom). Photo attachments stay
 * JPEG at high quality since they're already continuous-tone.
 *
 * RASTER_SCALE controls the supersampling factor: 2.5x the CSS pixel grid
 * hits ~300 dpi at Letter width, which is the print-industry sweet spot
 * for crisp text without ballooning file size. We floor at the device
 * pixel ratio so high-DPR screens never render *below* their native grid.
 */
const RASTER_SCALE = 2.5;
/**
 * Adaptive supersampling (SAB-038). Raster cost scales with the SQUARE of the
 * scale factor, and the overwhelming majority of report blocks are pure text
 * and vector rules that are already crisp well below 2.5x. Photographic and
 * chart-heavy blocks keep the full factor.
 */
const RASTER_SCALE_TEXT = 1.8;
const RASTER_SCALE_MIXED = 2.1;
const RASTER_SCALE_IMAGE = RASTER_SCALE;
const PHOTO_JPEG_QUALITY = 0.95;
/** Quality for image-heavy report blocks (photos/charts) encoded as JPEG. */
const BLOCK_JPEG_QUALITY = 0.92;
/** Quality for text/line-art blocks — high enough to be visually lossless. */
const TEXT_JPEG_QUALITY = 0.95;

const BLOCK_RENDER_TIMEOUT_MS = 20_000;
/** Overall watchdog for a single export run. */
const EXPORT_WATCHDOG_MS = 5 * 60_000;


function sanitizeForFilename(v: string | undefined | null): string {
  if (!v) return "";
  return v.replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
}

function buildFilename(invoiceNumber?: string, pkg?: ReportPackage) {
  const id = sanitizeForFilename(invoiceNumber) || "REPORT";
  const identity = getPackageIdentity(pkg);
  return `ClaimGuard-${identity.filenameSlug}-${id}.pdf`;
}

function buildSectionFilename(sectionTitle: string, fileNumber?: string) {
  const slug = sanitizeForFilename(sectionTitle) || "Section";
  const id = sanitizeForFilename(fileNumber) || "REPORT";
  return `ClaimGuard-${slug}-${id}.pdf`;
}

function findReportRoot(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  // The dedicated off-screen PDF publication tree in ReportPage owns the
  // [data-pdf-root] marker. The interactive tree deliberately does not
  // carry this attribute, so the exporter can never capture the live
  // website chrome.
  return document.querySelector<HTMLElement>(
    '[data-pdf-root]:not([data-pdf-clone="true"])',
  );
}


import { selectInvoiceSourceFiles } from "@/lib/supporting-documents";

/* ─────────────────────── invoice attachments ─────────────────────── */

function isPdfFile(f: ReviewFile): boolean {
  return (
    (f.mime_type ?? "").toLowerCase() === "application/pdf" ||
    /\.pdf$/i.test(f.file_name ?? "")
  );
}

function isImageFile(f: ReviewFile): boolean {
  const m = (f.mime_type ?? "").toLowerCase();
  if (m.startsWith("image/")) return true;
  return /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(f.file_name ?? "");
}

function pickInvoiceAttachments(files: ReviewFile[] | undefined): ReviewFile[] {
  if (!files?.length) return [];
  // Appendix A is the ORIGINAL INVOICE only. Supporting documents are evidence
  // and must never be merged into the invoice page sequence.
  const pool = selectInvoiceSourceFiles(files);
  return pool.filter((f) => isPdfFile(f) || isImageFile(f));
}

function pickSupportingPhotoAttachments(
  files: ReviewFile[] | undefined,
  invoiceIds: Set<string>,
): ReviewFile[] {
  if (!files?.length) return [];
  return files.filter((f) => {
    if (invoiceIds.has(f.id)) return false;
    return isImageFile(f);
  });
}

async function fetchBytes(file: ReviewFile): Promise<Uint8Array | null> {
  try {
    const url = await fileService.signedUrl(file.bucket, file.storage_path, 600);
    const res = await fetch(url);
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

async function waitForReportImages(root: HTMLElement, timeoutMs = 3500) {
  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (!img.src || (img.complete && img.naturalWidth > 0)) {
            resolve();
            return;
          }
          const done = () => {
            img.removeEventListener("load", done);
            img.removeEventListener("error", done);
            resolve();
          };
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
          window.setTimeout(done, timeoutMs);
        }),
    ),
  );
  const fonts = document.fonts;
  if (fonts?.ready) {
    await Promise.race([
      fonts.ready.catch(() => undefined),
      new Promise((resolve) => window.setTimeout(resolve, 1200)),
    ]);
  }
}

/* ─────────────────────── block collection ─────────────────────── */

const BLOCK_SELECTOR = '[data-pdf-include="true"]';

/**
 * Strict whitelist collection: only elements explicitly marked with
 * data-pdf-include="true" are captured. Nested marked elements are ignored
 * (outer wins) so we don't double-render children. Everything else in the
 * DOM — sales CTAs, upgrade cards, negotiation portal, action buttons —
 * is excluded by construction, not by opt-out.
 */
function collectTopBlocks(root: HTMLElement): HTMLElement[] {
  const all = Array.from(root.querySelectorAll<HTMLElement>(BLOCK_SELECTOR));
  const outer = all.filter(
    (el) => !all.some((a) => a !== el && a.contains(el)),
  );
  outer.sort(
    (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
  );
  return outer;
}

/** Recursively decompose blocks that don't fit on a single page. */
function decompose(
  el: HTMLElement,
  contentWmm: number,
  contentHmm: number,
): HTMLElement[] {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return [];
  // Project the on-screen aspect onto a page-width canvas.
  const projHmm = contentWmm * (rect.height / rect.width);
  if (projHmm <= contentHmm) return [el];
  const kids = Array.from(el.children).filter(
    (c) => c instanceof HTMLElement && c.getBoundingClientRect().height > 0,
  ) as HTMLElement[];
  if (kids.length === 0) return [el]; // leaf; will be scaled to fit
  const out: HTMLElement[] = [];
  for (const k of kids) out.push(...decompose(k, contentWmm, contentHmm));
  return out;
}

/**
 * Decomposition control (SAB-038).
 *
 * `decompose()` happily splits a chapter into dozens of small sub-blocks.
 * Every sub-block costs one full rasterization round-trip, so short adjacent
 * siblings are re-merged into a single wrapper when their combined projected
 * height still fits comfortably on a page. Only block-flow parents are
 * grouped — wrapping a flex/grid child would change its layout.
 */
export function groupAdjacentBlocks(
  blocks: HTMLElement[],
  projectHmm: (el: HTMLElement) => number,
  budgetHmm: number,
  isBlockFlowParent: (parent: HTMLElement) => boolean,
): HTMLElement[] {
  if (blocks.length < 2) return blocks;
  const out: HTMLElement[] = [];
  let i = 0;
  while (i < blocks.length) {
    const first = blocks[i];
    const parent = first.parentElement;
    let height = projectHmm(first);
    let j = i + 1;
    const run: HTMLElement[] = [first];
    while (
      parent &&
      isBlockFlowParent(parent) &&
      j < blocks.length &&
      blocks[j].parentElement === parent &&
      blocks[j].previousElementSibling === blocks[j - 1]
    ) {
      const h = projectHmm(blocks[j]);
      if (height + h > budgetHmm) break;
      height += h;
      run.push(blocks[j]);
      j++;
    }
    if (run.length > 1 && parent) {
      const wrapper = parent.ownerDocument.createElement("div");
      wrapper.setAttribute("data-pdf-group", "true");
      parent.insertBefore(wrapper, run[0]);
      for (const node of run) wrapper.appendChild(node);
      out.push(wrapper);
    } else {
      out.push(first);
    }
    i = j;
  }
  return out;
}



/* ─────────────────────── rendering ─────────────────────── */

interface RenderedBlock {
  dataUrl: string;
  /** Encoding of `dataUrl` (adaptive: JPEG for photographic blocks). */
  format?: "PNG" | "JPEG";

  wMm: number;
  hMm: number;
  /** When true, force a new PDF page before drawing this block. */
  breakBefore?: boolean;
  /** Optional chapter title used to update the running header on that page. */
  chapterTitle?: string;
  /**
   * When true, this block must render on the same PDF page as the previous
   * block. Used to keep a chapter's primary content welded to its header.
   */
  keepWithPrev?: boolean;
  /** DOM id of the top-level block this piece originated from (e.g. "package-contents"). */
  topBlockId?: string;
}

interface ImageAttachmentPage {
  file: ReviewFile;
  dataUrl: string;
  wMm: number;
  hMm: number;
  appendixTitle: string;
  /** When true, this page is part of the front-matter Original Invoice section. */
  isInvoiceFrontMatter?: boolean;
}

/**
 * True when a rendered block carries no visible content — every pixel is
 * white (or transparent) after downsampling. Used to suppress unintended
 * blank PDF pages without touching legitimate content, scaling, margins,
 * or raster quality: the check runs on a tiny 48px-wide copy, so cost is
 * negligible and the original canvas is never modified.
 */
function isBlankCanvas(canvas: HTMLCanvasElement): boolean {
  try {
    const w = Math.min(48, canvas.width);
    const h = Math.max(1, Math.round((canvas.height / canvas.width) * w));
    const probe = document.createElement("canvas");
    probe.width = w;
    probe.height = h;
    const ctx = probe.getContext("2d", { willReadFrequently: true });
    if (!ctx) return false;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(canvas, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    for (let i = 0; i < data.length; i += 4) {
      // Any pixel measurably darker/tinted than paper white counts as ink.
      if (data[i] < 248 || data[i + 1] < 248 || data[i + 2] < 248) return false;
    }
    return true;
  } catch {
    // Never let the guard break an export — assume the block has content.
    return false;
  }
}



function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(label)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) window.clearTimeout(timeoutId);
  });
}

/* ───────────── adaptive raster quality (SAB-038) ───────────── */

export type RasterProfile = "text" | "mixed" | "image";

/**
 * Classifies a block by how much of its area is occupied by raster/vector
 * media. Text-only blocks (the vast majority of the report) do not need
 * photographic supersampling.
 */
export function classifyRasterProfile(el: HTMLElement): RasterProfile {
  const explicit = el.getAttribute?.("data-pdf-quality");
  if (explicit === "text" || explicit === "mixed" || explicit === "image") return explicit;
  try {
    const rect = el.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (!(area > 0)) return "mixed";
    const media = Array.from(el.querySelectorAll<HTMLElement>("img, canvas, svg, video"));
    if (el.matches("img, canvas, svg, video")) media.push(el);
    let mediaArea = 0;
    for (const m of media) {
      const r = m.getBoundingClientRect();
      mediaArea += Math.max(0, r.width) * Math.max(0, r.height);
    }
    const coverage = mediaArea / area;
    if (coverage >= 0.4) return "image";
    if (coverage >= 0.08) return "mixed";
    return "text";
  } catch {
    return "mixed";
  }
}

export function rasterScaleForProfile(profile: RasterProfile): number {
  if (profile === "text") return RASTER_SCALE_TEXT;
  if (profile === "image") return RASTER_SCALE_IMAGE;
  return RASTER_SCALE_MIXED;
}

/* ───────────── isolated render stage (SAB-038) ───────────── */

type Html2Canvas = (
  element: HTMLElement,
  options: Record<string, unknown>,
) => Promise<HTMLCanvasElement>;

interface RenderStage {
  kind: "isolated" | "inline";
  render(el: HTMLElement, scale: number): Promise<HTMLCanvasElement>;
  destroy(): void;
}

function h2cOptions(el: HTMLElement, scale: number, width: number): Record<string, unknown> {
  return {
    scale,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    windowWidth: Math.ceil(el.getBoundingClientRect().width || width),
    imageTimeout: 8_000,
    letterRendering: true,
  };
}

/**
 * Records which path rendered the most recent block ("isolated" staging
 * iframe vs the legacy whole-document clone). Diagnostics only.
 */
let lastRenderPath: "isolated" | "inline" = "isolated";

/** Per-block stage timing split (diagnostics only). */
let lastStageTiming = { importMs: 0, layoutMs: 0, h2cMs: 0 };

/** Legacy path: html2canvas clones the whole owner document per block. */
function createInlineStage(html2canvas: Html2Canvas, width: number): RenderStage {
  return {
    kind: "inline",
    render: async (el, scale) => {
      lastRenderPath = "inline";
      const t = performance.now();
      const canvas = await html2canvas(el, h2cOptions(el, scale, width));
      lastStageTiming = { importMs: 0, layoutMs: 0, h2cMs: performance.now() - t };
      return canvas;
    },
    destroy: () => undefined,
  };
}


/**
 * Table-family ancestors cannot be replicated with generic <div> shells, so
 * the staging path imports the whole enclosing <table> instead (preserving
 * column widths exactly) and rasterizes the mapped descendant inside it.
 * Returns the outermost <table> between `el` and `root`, if any.
 */
function enclosingTableRoot(el: HTMLElement, root: HTMLElement): HTMLElement | null {
  let cur = el.parentElement;
  let found: HTMLElement | null = null;
  while (cur && cur !== root) {
    if (cur.tagName === "TABLE") found = cur;
    cur = cur.parentElement;
  }
  return found;
}


/**
 * Rebuilds the block's ancestor chain (classes + inline styles only) inside
 * the staging document so inherited layout, width and typography survive.
 */
function buildAncestorShell(
  el: HTMLElement,
  root: HTMLElement,
  doc: Document,
): { outer: HTMLElement; slot: HTMLElement } {
  const chain: HTMLElement[] = [];
  let cur = el.parentElement;
  while (cur && cur !== root && chain.length < 16) {
    chain.push(cur);
    cur = cur.parentElement;
  }
  const outer = doc.createElement("div");
  let slot: HTMLElement = outer;
  for (const anc of chain.reverse()) {
    const shell = doc.createElement("div");
    shell.className = typeof anc.className === "string" ? anc.className : "";
    if (anc.style?.cssText) shell.style.cssText = anc.style.cssText;
    slot.appendChild(shell);
    slot = shell;
  }
  return { outer, slot };
}

async function waitForStageImages(root: HTMLElement, timeoutMs = 2500): Promise<void> {
  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>("img"));
  if (imgs.length === 0) return;
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (!img.src || (img.complete && img.naturalWidth > 0)) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
          window.setTimeout(done, timeoutMs);
        }),
    ),
  );
}

/**
 * ONE staging document per export run.
 *
 * html2canvas clones the *entire owner document* for every call. Rendering
 * ~250 blocks out of the live 88k-px report therefore re-cloned and
 * re-styled the whole report 250 times — the dominant cost measured in
 * SAB-038. Here we build a single hidden iframe once, stylesheets copied,
 * fonts awaited once; each block is imported into it alone, so html2canvas
 * clones a document containing just that block.
 *
 * Falls back to the legacy in-page path whenever the iframe cannot be
 * established, so exports can never break because of this optimization.
 */
async function createRenderStage(
  root: HTMLElement,
  html2canvas: Html2Canvas,
): Promise<RenderStage> {
  const width = Math.ceil(root.getBoundingClientRect().width || 1180);
  const inline = createInlineStage(html2canvas, width);
  if (typeof document === "undefined") return inline;
  try {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("title", "PDF render stage");
    iframe.style.cssText = `position:fixed;left:-20000px;top:0;width:${width}px;height:1200px;border:0;opacity:0;pointer-events:none;`;
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    if (!doc) {
      iframe.remove();
      return inline;
    }
    doc.open();
    doc.write("<!doctype html><html><head></head><body></body></html>");
    doc.close();
    const sdoc = iframe.contentDocument;
    if (!sdoc?.body) {
      iframe.remove();
      return inline;
    }

    const pending: Promise<void>[] = [];
    document
      .querySelectorAll<HTMLElement>('link[rel="stylesheet"], style')
      .forEach((node) => {
        const copy = sdoc.importNode(node, true) as HTMLElement;
        sdoc.head.appendChild(copy);
        if (copy.tagName === "LINK") {
          pending.push(
            new Promise<void>((resolve) => {
              const done = () => resolve();
              copy.addEventListener("load", done, { once: true });
              copy.addEventListener("error", done, { once: true });
              window.setTimeout(done, 4000);
            }),
          );
        }
      });

    sdoc.documentElement.className = document.documentElement.className;
    for (const attr of Array.from(document.documentElement.attributes)) {
      if (attr.name.startsWith("data-")) {
        sdoc.documentElement.setAttribute(attr.name, attr.value);
      }
    }
    sdoc.body.className = document.body.className;
    sdoc.body.style.cssText = "margin:0;padding:0;background:#ffffff;";

    const wrapper = sdoc.createElement("div");
    wrapper.className = typeof root.className === "string" ? root.className : "";
    wrapper.style.cssText = `width:${width}px;max-width:none;background:#ffffff;`;
    sdoc.body.appendChild(wrapper);

    await Promise.all(pending);
    const stageFonts = (sdoc as Document & { fonts?: FontFaceSet }).fonts;
    if (stageFonts?.ready) {
      await Promise.race([
        stageFonts.ready.catch(() => undefined),
        new Promise((r) => window.setTimeout(r, 2000)),
      ]);
    }

    return {
      kind: "isolated",
      async render(el, scale) {
        lastRenderPath = "isolated";
        const t0 = performance.now();
        // Blocks inside a table are staged together with their whole <table>
        // so column widths resolve exactly as they do on screen.
        const tableRoot = enclosingTableRoot(el, root);
        const importSource = tableRoot ?? el;
        const MARK = "data-pdf-stage-target";
        if (tableRoot) el.setAttribute(MARK, "1");
        const shell = buildAncestorShell(importSource, root, sdoc);
        let imported: HTMLElement;
        try {
          imported = sdoc.importNode(importSource, true) as HTMLElement;
        } finally {
          if (tableRoot) el.removeAttribute(MARK);
        }
        shell.slot.appendChild(imported);
        wrapper.appendChild(shell.outer);
        const t1 = performance.now();
        try {
          const target = (
            tableRoot ? imported.querySelector(`[${MARK}="1"]`) : imported
          ) as HTMLElement | null;
          if (!target) return inline.render(el, scale);
          iframe.style.height = `${Math.max(400, Math.ceil(wrapper.scrollHeight))}px`;
          await new Promise((r) =>
            typeof requestAnimationFrame === "function"
              ? requestAnimationFrame(() => r(null))
              : r(null),
          );
          await waitForStageImages(target);
          const rect = target.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return inline.render(el, scale);
          const t2 = performance.now();
          const canvas = await html2canvas(target, h2cOptions(target, scale, width));
          lastStageTiming = {
            importMs: t1 - t0,
            layoutMs: t2 - t1,
            h2cMs: performance.now() - t2,
          };
          return canvas;
        } finally {
          shell.outer.remove();
        }
      },


      destroy() {
        try {
          iframe.remove();
        } catch {
          /* never let teardown break an export */
        }
      },
    };
  } catch {
    return inline;
  }
}

/**
 * Per-block profiling sink (SAB-038). Populated only while performance
 * diagnostics are enabled; never affects rendering output.
 */
export interface BlockPerfEntry {
  renderMs: number;
  encodeMs: number;
  megapixels: number;
  scale: number;
  path: "isolated" | "inline";
  profile: RasterProfile;
  kb: number;
  /** Human-identifiable origin of the block (chapter anchor / id / classes). */
  label: string;
  /** Element count of the captured subtree. */
  nodes: number;
  importMs: number;
  layoutMs: number;
  h2cMs: number;
  cssPx: string;
}
let blockPerfSink: ((e: BlockPerfEntry) => void) | null = null;

/** Diagnostics-only: best-effort human label for a block. */
function labelForBlock(el: HTMLElement): string {
  try {
    const parts: string[] = [];
    const chapter = el.closest?.("[id^='chapter-']") as HTMLElement | null;
    if (chapter?.id) parts.push(chapter.id);
    if (el.id) parts.push(`#${el.id}`);
    const cls = typeof el.className === "string" ? el.className.trim().split(/\s+/) : [];
    parts.push(`${el.tagName.toLowerCase()}.${cls.slice(0, 3).join(".")}`);
    const heading = el.querySelector("h1,h2,h3,h4")?.textContent?.trim().slice(0, 40);
    if (heading) parts.push(`"${heading}"`);
    return parts.join(" ");
  } catch {
    return "?";
  }
}


async function renderBlock(
  el: HTMLElement,

  stage: RenderStage,
  contentWmm: number,
  contentHmm: number,
  maxHmm?: number,
  scaleOverride?: number,
): Promise<RenderedBlock | null> {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  const dpr =
    typeof window !== "undefined" && window.devicePixelRatio
      ? window.devicePixelRatio
      : 1;
  const profile = classifyRasterProfile(el);
  const baseScale = scaleOverride ?? rasterScaleForProfile(profile);
  // Never render below the device pixel grid, but never let a high-DPR
  // screen push a text block back up to photographic supersampling either.
  const effectiveDpr = Math.min(dpr, RASTER_SCALE_MIXED);
  const scale = Math.max(baseScale, effectiveDpr);

  const tRender = typeof performance !== "undefined" ? performance.now() : 0;
  const canvas = await withTimeout(
    stage.render(el, scale),
    BLOCK_RENDER_TIMEOUT_MS,
    "A report section took too long to render and was skipped",
  );
  const renderMs = (typeof performance !== "undefined" ? performance.now() : 0) - tRender;
  if (canvas.width === 0 || canvas.height === 0) return null;
  // Blank-block guard: some wrappers survive DOM decomposition with real
  // geometry but no ink (spacer wrappers, package-gated sections that render
  // nothing, tall empty grid tracks). Emitting them manufactures blank PDF
  // pages, so drop any raster that contains no visible content at all.
  if (isBlankCanvas(canvas)) {
    canvas.width = 0;
    canvas.height = 0;
    return null;
  }


  let wMm = contentWmm;
  let hMm = contentWmm * (canvas.height / canvas.width);
  const ceiling = Math.min(contentHmm, maxHmm ?? contentHmm);
  if (hMm > ceiling) {
    const s = ceiling / hMm;
    hMm = ceiling;
    wMm = wMm * s;
  }
  // Stream and release: encode once, immediately, then drop the bitmap.
  //
  // Every block is encoded as high-quality JPEG. jsPDF has to fully decode and
  // re-deflate PNG payloads (and split an alpha mask) in JavaScript, which was
  // measured as the single largest stage of a full export; JPEG bytes are
  // embedded verbatim as DCTDecode. At q=0.95 over a 1.8-2.5x supersampled
  // raster the difference is invisible at print scale, and the blocks are
  // rendered on an opaque white background so no alpha is lost.
  const megapixels = (canvas.width * canvas.height) / 1e6;
  const tEncode = typeof performance !== "undefined" ? performance.now() : 0;
  const dataUrl = canvas.toDataURL(
    "image/jpeg",
    profile === "image" ? BLOCK_JPEG_QUALITY : TEXT_JPEG_QUALITY,
  );
  const encodeMs = (typeof performance !== "undefined" ? performance.now() : 0) - tEncode;
  const format: "PNG" | "JPEG" = "JPEG";

  if (blockPerfSink) {
    blockPerfSink({
      renderMs,
      encodeMs,
      megapixels,
      scale,
      path: lastRenderPath,
      profile,
      kb: Math.round(dataUrl.length / 1024),
      label: labelForBlock(el),
      nodes: el.querySelectorAll("*").length,
      importMs: lastStageTiming.importMs,
      layoutMs: lastStageTiming.layoutMs,
      h2cMs: lastStageTiming.h2cMs,
      cssPx: `${Math.round(rect.width)}x${Math.round(rect.height)}`,
    });
  }

  // Release the backing canvas eagerly so long chapters (many sub-blocks)
  // don't retain every full-resolution bitmap in memory until GC catches up.
  canvas.width = 0;
  canvas.height = 0;
  return { dataUrl, format, wMm, hMm };
}



/**
 * Prepare a cloned capture root so it reads like a printed document:
 *   • every <details> is forced open
 *   • Radix accordions with data-state="closed" flip to "open" and lose
 *     their hidden attribute so nested content renders
 *   • [hidden] and aria-hidden="true" content the app collapses in the
 *     interactive view becomes visible so the PDF is self-contained
 */
function expandCollapsibles(root: HTMLElement) {
  // <details>
  root.querySelectorAll<HTMLDetailsElement>("details").forEach((d) => {
    d.open = true;
  });
  // Radix / shadcn accordions
  root
    .querySelectorAll<HTMLElement>(
      '[data-state="closed"], [data-radix-collapsible-content], [data-radix-accordion-content]',
    )
    .forEach((el) => {
      el.setAttribute("data-state", "open");
      el.removeAttribute("hidden");
      el.style.display = "";
      el.style.height = "auto";
      el.style.overflow = "visible";
    });
}

/* ─────────────────────── page chrome ─────────────────────── */

function drawChrome(
  pdf: import("jspdf").jsPDF,
  pageWmm: number,
  pageHmm: number,
  pageIndex: number,
  totalPages: number,
  fileLabel: string,
  sectionTitle?: string,
  versionLine?: string,
  packageLabel?: string,
) {
  const marginX = MARGIN_MM;
  pdf.setDrawColor(220);
  pdf.setLineWidth(0.2);
  pdf.line(marginX, HEADER_H_MM, pageWmm - marginX, HEADER_H_MM);
  pdf.setFontSize(8);
  pdf.setTextColor(90);
  pdf.text("ClaimGuard", marginX, HEADER_H_MM - 5);
  pdf.setTextColor(140);
  const brand = packageLabel ?? "Claim Intelligence Package™";
  const subLine = sectionTitle ? `${brand} · ${sectionTitle}` : brand;
  pdf.text(subLine, marginX, HEADER_H_MM - 1);
  if (fileLabel) {
    const rw = pdf.getTextWidth(fileLabel);
    pdf.setTextColor(90);
    pdf.text(fileLabel, pageWmm - marginX - rw, HEADER_H_MM - 5);
  }
  const pageLabel = `Page ${pageIndex + 1} of ${totalPages}`;
  const pw = pdf.getTextWidth(pageLabel);
  pdf.setTextColor(140);
  pdf.text(pageLabel, pageWmm - marginX - pw, HEADER_H_MM - 1);

  const footerTopY = pageHmm - FOOTER_H_MM;
  pdf.setDrawColor(220);
  pdf.line(marginX, footerTopY, pageWmm - marginX, footerTopY);
  pdf.setFontSize(8);
  pdf.setTextColor(90);
  pdf.text(
    "Prepared by ClaimGuard Professional Review Services",
    marginX,
    footerTopY + 5,
  );
  pdf.setTextColor(140);
  pdf.text(
    "AI Invoice Reviews & Full-Service Negotiation",
    marginX,
    footerTopY + 9,
  );
  const confLabel = "Confidential";
  const cw = pdf.getTextWidth(confLabel);
  pdf.setTextColor(90);
  pdf.text(confLabel, pageWmm - marginX - cw, footerTopY + 5);
  if (versionLine) {
    pdf.setFontSize(6.5);
    pdf.setTextColor(170);
    const vw = pdf.getTextWidth(versionLine);
    pdf.text(versionLine, pageWmm - marginX - vw, footerTopY + 9);
  }
}

/* ─────────────────────── balance / reflow ─────────────────────── */

/**
 * Intra-chapter block reordering to eliminate excessive whitespace tails.
 *
 * Rules:
 *   • Chapter order is preserved. A chapter starts at the first block that
 *     carries a `chapterTitle` and runs until the next `chapterTitle` block.
 *   • Chapter headers (`breakBefore` + `chapterTitle`) stay at the head of
 *     their chapter.
 *   • Blocks marked `keepWithPrev` remain immediately after their
 *     predecessor (welded content — table/timeline/grid fit under header).
 *   • Cards are never split. Every block is placed atomically.
 *   • Within a chapter, if placing the next block in order would leave a
 *     page tail > 35% of the printable area empty, we scan forward *inside
 *     the same chapter* for the first block that fits in the tail and
 *     hoist it up. The passed-over block is placed next.
 */
function reflowForBalance(
  input: RenderedBlock[],
  contentTop: number,
  contentBottom: number,
  contentHmm: number,
  minSectionAfterBannerMm: number,
): RenderedBlock[] {
  if (input.length === 0) return input;
  // (tail-balancing removed in V1.1 — natural document order only)

  // Split into chapter groups.
  const groups: RenderedBlock[][] = [];
  let cur: RenderedBlock[] = [];
  for (const b of input) {
    if (b.chapterTitle && cur.length > 0) {
      groups.push(cur);
      cur = [];
    }
    cur.push(b);
  }
  if (cur.length) groups.push(cur);

  const out: RenderedBlock[] = [];
  let simY = contentTop;
  const advance = (h: number) => {
    simY += h + GAP_MM;
  };
  const newPage = () => {
    simY = contentTop;
  };

  // V1.1 chapter layout standardization: a major chapter ALWAYS opens a
  // fresh page. No banner ever shares a page with the previous chapter's
  // tail, and content is never reordered to backfill whitespace — any
  // remaining space belongs to the chapter that ended early.
  void minSectionAfterBannerMm;

  for (const group of groups) {
    const head = group[0];
    const atPageTop = () => simY <= contentTop + 0.1;
    if (head.breakBefore) {
      if (!atPageTop()) newPage();
    } else if (!atPageTop() && simY + head.hMm > contentBottom + 0.1) {
      newPage();
    }
    out.push(head);
    advance(head.hMm);

    // Pin any leading keepWithPrev welded blocks.
    let i = 1;
    while (i < group.length && group[i].keepWithPrev) {
      out.push(group[i]);
      advance(group[i].hMm);
      i++;
    }

    // Body blocks flow in document order; subsections, cards, tables and
    // charts break only when they cannot fit the remaining page.
    for (const b of group.slice(i)) {
      if (!atPageTop() && simY + b.hMm > contentBottom + 0.1) newPage();
      out.push(b);
      advance(b.hMm);
    }
  }

  return out;
}

/* ─────────────────────── main entry ─────────────────────── */

async function generatePdfFromDom(
  opts: Options,
): Promise<{
  mergedInvoicePdfs: number;
  renderedInvoiceImages: number;
  placeholderRendered: boolean;
}> {
  const sectionMode = !!opts.rootElement;
  const perf = createPerfTimeline();
  perf.start("total");

  // Hoisted state — must be reachable from the outer `finally` and from
  // `emitPerfSummary`, both of which can run before these variables have
  // been assigned by the pipeline (e.g. an exception during libs:import).
  let releaseRoot: () => void = () => undefined;
  let stage: RenderStage | null = null;

  let status: string = "in-progress";
  let totalPages = 0;
  let renderedRef: RenderedBlock[] = [];
  let pdfAttachmentsRef: { file: ReviewFile; bytes: Uint8Array; pageCount: number }[] = [];
  let resultBytes = 0;
  const blockPerf: BlockPerfEntry[] = [];
  let summaryEmitted = false;


  const perfDiagnosticsEnabled = (): boolean => {
    try {
      const env = (import.meta as { env?: { DEV?: boolean } }).env;
      if (env?.DEV) return true;
    } catch {
      /* ignore */
    }
    if (typeof window !== "undefined") {
      try {
        const w = window as unknown as { __CLAIMGUARD_PDF_PERF__?: unknown };
        if (w.__CLAIMGUARD_PDF_PERF__ === true) return true;
      } catch {
        /* ignore */
      }
      try {
        if (window.localStorage?.getItem("CLAIMGUARD_PDF_PERF") === "true") {
          return true;
        }
      } catch {
        /* ignore — storage may be blocked */
      }
    }
    return false;
  };

  const emitPerfSummary = () => {
    if (summaryEmitted) return;
    summaryEmitted = true;
    try {
      perf.end("total");
    } catch {
      /* ignore */
    }
    if (!perfDiagnosticsEnabled()) return;
    try {
      const entries = perf.entries();
      const total = entries.find((s) => s.name === "total")?.ms ?? 0;
      const nonTotal = entries.filter((s) => s.name !== "total");
      const longest = [...nonTotal].sort((a, b) => b.ms - a.ms)[0];
      const invoiceMerge = entries.find((s) => s.name === "pdflib:merge")?.ms ?? 0;
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `%cPDF Export Performance — ${total.toFixed(0)} ms · ${status}`,
        "color:#2563eb;font-weight:bold",
      );
      for (const s of nonTotal) {
        // eslint-disable-next-line no-console
        console.log(`  ${s.name.padEnd(24)} ${s.ms.toFixed(0).padStart(6)} ms`);
      }
      // eslint-disable-next-line no-console
      console.log(`Status: ${status}`);
      // eslint-disable-next-line no-console
      console.log(
        `Longest stage: ${longest?.name ?? "—"} (${longest?.ms?.toFixed(0) ?? 0} ms)`,
      );
      // eslint-disable-next-line no-console
      console.log(`Total pages: ${totalPages ?? 0}`);
      // eslint-disable-next-line no-console
      console.log(`Rendered blocks: ${renderedRef?.length ?? 0}`);
      // eslint-disable-next-line no-console
      console.log(`PDF attachments merged: ${pdfAttachmentsRef?.length ?? 0}`);
      // eslint-disable-next-line no-console
      console.log(`Invoice merge time: ${invoiceMerge.toFixed(0)} ms`);
      // eslint-disable-next-line no-console
      console.log(`PDF size: ${((resultBytes ?? 0) / 1024).toFixed(1)} KB`);
      if (blockPerf.length) {
        const sum = (f: (e: BlockPerfEntry) => number) =>
          blockPerf.reduce((a, e) => a + f(e), 0);
        const byProfile: Record<string, { n: number; render: number; encode: number }> = {};
        for (const e of blockPerf) {
          const b = (byProfile[`${e.profile}/${e.path}`] ??= { n: 0, render: 0, encode: 0 });
          b.n++;
          b.render += e.renderMs;
          b.encode += e.encodeMs;
        }
        // eslint-disable-next-line no-console
        console.log(
          `Block raster: ${blockPerf.length} blocks · rasterize ${sum((e) => e.renderMs).toFixed(0)} ms · encode ${sum((e) => e.encodeMs).toFixed(0)} ms · ${sum((e) => e.megapixels).toFixed(1)} MP`,
        );
        for (const [p, b] of Object.entries(byProfile)) {
          // eslint-disable-next-line no-console
          console.log(
            `  profile ${p.padEnd(6)} n=${String(b.n).padStart(3)} rasterize ${b.render.toFixed(0).padStart(6)} ms  encode ${b.encode.toFixed(0).padStart(6)} ms`,
          );
        }
        for (const e of [...blockPerf].sort((a, b) => b.renderMs + b.encodeMs - (a.renderMs + a.encodeMs)).slice(0, 10)) {
          // eslint-disable-next-line no-console
          console.log(
            `  top ${e.profile.padEnd(6)} ${e.path.padEnd(8)} @${e.scale} ${e.cssPx} ${e.megapixels.toFixed(1)}MP nodes=${e.nodes} import ${e.importMs.toFixed(0)}ms layout ${e.layoutMs.toFixed(0)}ms h2c ${e.h2cMs.toFixed(0)}ms total ${e.renderMs.toFixed(0)}ms encode ${e.encodeMs.toFixed(0)}ms ${e.kb}KB :: ${e.label}`,
          );
        }

      }

      // eslint-disable-next-line no-console
      console.groupEnd();
    } catch {
      /* instrumentation must never break exports */
    }
  };

  try {
    status = "failed:libs-import";
    perf.start("libs:import");
    const [{ default: html2canvas }, jsPDFmod] = await Promise.all([
      import("html2canvas-pro"),
      import("jspdf"),
    ]);
    const { jsPDF } = jsPDFmod;
    perf.end("libs:import");

    // Resolve the capture root. Priority:
    //   1. Section mode — clone the caller's live element so PDF mutations
    //      (hiding .no-print, expanding collapsibles) don't leak into the UI.
    //   2. Registered on-demand mount host — materialize a fresh PDF
    //      publication tree just for this export, then unmount it. No
    //      "hidden" tree lives in the DOM while the user browses.
    //   3. Legacy fallback — find an existing [data-pdf-root] and clone it.
    status = "failed:tree-mount";
    perf.start("tree:mount");
    let root: HTMLElement;

  if (opts.rootElement) {
    const src = opts.rootElement;
    const clone = src.cloneNode(true) as HTMLElement;
    const rect = src.getBoundingClientRect();
    clone.id = "report-pdf-capture-root";
    clone.dataset.pdfClone = "true";
    clone.removeAttribute("data-pdf-root");
    clone.classList.add("pdf-generating");
    clone.style.position = "absolute";
    clone.style.left = "-10000px";
    clone.style.top = "0";
    clone.style.width = `${Math.ceil(rect.width || 1180)}px`;
    clone.style.maxWidth = "none";
    clone.style.pointerEvents = "none";
    clone.style.background = "#ffffff";
    document.body.appendChild(clone);
    root = clone;
    releaseRoot = () => clone.remove();
  } else if (pdfTreeMountFn) {
    const handle = await pdfTreeMountFn(opts.packageType ?? "complete");
    root = handle.root;
    releaseRoot = handle.release;
  } else {
    // Legacy path: existing hidden [data-pdf-root] tree gets cloned.
    const sourceRoot = findReportRoot();
    if (!sourceRoot) throw new Error("Report content not found");
    const clone = sourceRoot.cloneNode(true) as HTMLElement;
    const sourceRect = sourceRoot.getBoundingClientRect();
    clone.id = "report-pdf-capture-root";
    clone.dataset.pdfClone = "true";
    clone.removeAttribute("data-pdf-root");
    clone.classList.add("pdf-generating");
    clone.style.position = "absolute";
    clone.style.left = "-10000px";
    clone.style.top = "0";
    clone.style.width = `${Math.ceil(sourceRect.width || 1180)}px`;
    clone.style.maxWidth = "none";
    clone.style.pointerEvents = "none";
    clone.style.background = "#ffffff";
    document.body.appendChild(clone);
    root = clone;
    releaseRoot = () => clone.remove();
  }
  await new Promise((r) => requestAnimationFrame(() => r(null)));
  perf.end("tree:mount");

  const hidden: { el: HTMLElement; prev: string }[] = [];
  root
    .querySelectorAll<HTMLElement>('.no-print, [data-pdf-exclude="true"]')
    .forEach((el) => {
      hidden.push({ el, prev: el.style.display });
      el.style.display = "none";
    });

  // Auto-expand every collapsible so the PDF reads like a bound document,
  // not an interactive webpage screenshot. Must happen BEFORE we wait for
  // images, so imagery inside collapsed sections gets loaded and captured.
  expandCollapsibles(root);

  perf.start("images:wait");
  await waitForReportImages(root);
  perf.end("images:wait");


  const pageSize: PageSize = opts.pageSize ?? "a4";
  const pdf = new jsPDF({ unit: "mm", format: pageSize, orientation: "portrait" });
  const reportMeta = readAmbientReportMetadata();
  const versionLine = reportMeta ? shortVersionString(reportMeta) : undefined;
  const identity = getPackageIdentity(opts.packageType);
  const packageLabel = identity.title;
  if (reportMeta) {
    try {
      pdf.setProperties({
        title: `${identity.titlePlain}${opts.fileNumber ? ` — File ${opts.fileNumber}` : ""}`,
        subject: `${identity.title} v${reportMeta.package_version}`,
        author: "ClaimGuard Professional Review Services",
        creator: `ClaimGuard Intelligence Engine v${reportMeta.intelligence_engine_version}`,
        keywords: [
          `package:${identity.key}`,
          `report_uuid:${reportMeta.report_uuid}`,
          `package_version:${reportMeta.package_version}`,
          `intelligence_engine:${reportMeta.intelligence_engine_version}`,
          `legal_dataset:${reportMeta.legal_dataset_version}`,
          `market_dataset:${reportMeta.market_dataset_version}`,
          `ocr_engine:${reportMeta.ocr_engine}`,
          `confidence_engine:${reportMeta.confidence_engine_version}`,
          `generated_at:${reportMeta.generated_at}`,
        ].join(", "),
      });
    } catch {
      // non-fatal — metadata is best-effort
    }
  }
  const pageWmm = pdf.internal.pageSize.getWidth();
  const pageHmm = pdf.internal.pageSize.getHeight();
  const contentTop = HEADER_H_MM + 6;
  const contentBottom = pageHmm - FOOTER_H_MM - 4;
  const contentWmm = pageWmm - MARGIN_MM * 2;
  const contentHmm = contentBottom - contentTop;

  const fileLabel = opts.fileNumber ? `File ${opts.fileNumber}` : "";
  const rendered: RenderedBlock[] = [];
  renderedRef = rendered;


  const renderFailures: { tag: string; cls: string; error: string }[] = [];
  try {
    let topBlocks = sectionMode ? [root] : collectTopBlocks(root);
    if (opts.includeAnchors && opts.includeAnchors.length > 0) {
      const allow = new Set(opts.includeAnchors);
      const preFilterChapterIds = topBlocks
        .map((el) => el.id ?? "")
        .filter((id) => id.startsWith("chapter-") || id === "appendix-a");
      topBlocks = topBlocks.filter((el) => {
        const id = el.id ?? "";
        // Only chapter blocks and appendix-a are gated; keep cover/TOC/closing.
        if (id.startsWith("chapter-") || id === "appendix-a") return allow.has(id);
        return true;
      });
      const matchedChapterIds = topBlocks
        .map((el) => el.id ?? "")
        .filter((id) => id.startsWith("chapter-") || id === "appendix-a");
      if (perfDiagnosticsEnabled()) {
        // eslint-disable-next-line no-console
        console.log("[PDF] Package filter", {
          package: opts.packageType ?? null,
          requestedAnchors: [...allow],
          detectedChapterIds: preFilterChapterIds,
          matchedChapterIds,
          matchedCount: matchedChapterIds.length,
        });
      }
      if (matchedChapterIds.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("PDF PACKAGE FILTER WARNING", {
          package: opts.packageType ?? null,
          requestedAnchors: [...allow],
          detectedChapterIds: preFilterChapterIds,
          matchedChapterIds,
          matchedCount: 0,
          hint:
            "No chapter elements matched the requested anchors. DossierChapter is likely missing its id in PDF mode.",
        });
      }
    }
    if (topBlocks.length === 0) {
      throw new Error(
        'No exportable content found (no elements with data-pdf-include="true"). Report may still be loading.',
      );
    }
    // Flatten each top-level chapter into paginatable sub-blocks, then
    // propagate the chapter's page-break intent onto the first sub-block.
    // Each top block that opts in with data-pdf-page-break="before" gets
    // its own PDF page (or continues on a fresh page if it would splash
    // across a page boundary).
    interface FlatEntry {
      el: HTMLElement;
      breakBefore: boolean;
      chapterTitle?: string;
      /** Optional height ceiling (mm) applied at render time. */
      maxHmm?: number;
      /** When true, must ride on the same PDF page as the previous entry. */
      keepWithPrev?: boolean;
      /** Id of the originating top-level block. */
      topBlockId?: string;
    }
    const flatEntries: FlatEntry[] = [];
    let groupedFrom = 0;
    let groupedTo = 0;

    for (const b of topBlocks) {
      const breakBefore = b.getAttribute("data-pdf-page-break") === "before";
      // Prefer the DossierChapter's title if present; otherwise pull the
      // first h2 in the block.
      const titleEl =
        b.querySelector<HTMLElement>("[data-pdf-chapter-title]") ??
        b.querySelector<HTMLElement>("h2");
      const chapterTitle = titleEl?.textContent?.trim() || undefined;
      const rawKids = decompose(b, contentWmm, contentHmm);
      // Re-merge short adjacent siblings so we rasterize fewer, larger blocks.
      const kids = groupAdjacentBlocks(
        rawKids,
        (el) => {
          const r = el.getBoundingClientRect();
          return r.width > 0 ? contentWmm * (r.height / r.width) : 0;
        },
        contentHmm * 0.92,
        (parent) => {
          try {
            const display = getComputedStyle(parent).display;
            return display === "block" || display === "flow-root";
          } catch {
            return false;
          }
        },
      );
      groupedFrom += rawKids.length;
      groupedTo += kids.length;
      kids.forEach((k, i) =>
        flatEntries.push({
          el: k,
          breakBefore: i === 0 && breakBefore,
          chapterTitle: i === 0 ? chapterTitle : undefined,
          topBlockId: b.id || undefined,
        }),
      );

    }


    // Keep-with-next: a chapter header must never render alone at the top of
    // a fresh PDF page with its primary content pushed to the next page.
    // Strategy, in order:
    //   1. If the header + immediately following block fit together, done.
    //   2. Otherwise try to decompose the following block into its children
    //      (until the first child fits under the header).
    //   3. If decomposition is not possible (monolithic table / timeline /
    //      chart / grid), constrain that block's rendered height so it fits
    //      under the header on the same page, and mark it keepWithPrev so
    //      the layout pass will not push it to the next page.
    const projHmm = (el: HTMLElement): number => {
      const r = el.getBoundingClientRect();
      if (r.width <= 0) return 0;
      return contentWmm * (r.height / r.width);
    };
    for (let i = 0; i < flatEntries.length; i++) {
      const cur = flatEntries[i];
      if (!cur.chapterTitle) continue;
      const headerH = projHmm(cur.el);
      if (headerH >= contentHmm) continue; // header itself > a page; nothing to do
      let safety = 6;
      let decomposed = false;
      while (
        safety-- > 0 &&
        i + 1 < flatEntries.length &&
        !flatEntries[i + 1].chapterTitle
      ) {
        const next = flatEntries[i + 1];
        const nextH = projHmm(next.el);
        if (headerH + GAP_MM + nextH <= contentHmm) {
          decomposed = true;
          break;
        }
        const kids = Array.from(next.el.children).filter(
          (c) => c instanceof HTMLElement && c.getBoundingClientRect().height > 0,
        ) as HTMLElement[];
        if (kids.length < 2) break;
        flatEntries.splice(
          i + 1,
          1,
          ...kids.map((k) => ({
            el: k,
            breakBefore: false,
            chapterTitle: undefined as string | undefined,
            topBlockId: next.topBlockId,
          })),

        );
      }
      // Step 3: block is indivisible AND doesn't fit — constrain height &
      // weld to header. Prevents "header alone on a mostly blank page while
      // the big table sits alone on the next page".
      if (!decomposed && i + 1 < flatEntries.length && !flatEntries[i + 1].chapterTitle) {
        const budget = contentHmm - headerH - GAP_MM;
        // Only weld if there's a sane amount of room under the header
        // (>= 25% of the page). Otherwise the header itself is oversized
        // and we accept the split.
        if (budget >= contentHmm * 0.25) {
          flatEntries[i + 1].maxHmm = budget;
          flatEntries[i + 1].keepWithPrev = true;
        }
      }
    }
    // Section mode keeps its established quality baseline.
    const scaleOverride = sectionMode ? 1.9 : undefined;
    const totalEntries = flatEntries.length;
    let renderedCount = 0;
    status = "failed:html2canvas";
    // ONE staging document for the whole run — see createRenderStage().
    perf.start("stage:prepare");
    opts.onProgress?.({ phase: "preparing", rendered: 0, total: totalEntries });
    stage = await createRenderStage(root, html2canvas as Html2Canvas);
    perf.end("stage:prepare");
    if (perfDiagnosticsEnabled()) {
      // eslint-disable-next-line no-console
      console.log("[PDF] Render plan", {
        stage: stage.kind,
        blocksBeforeGrouping: groupedFrom,
        blocksAfterGrouping: groupedTo,
        blocksToRender: totalEntries,
      });
    }
    const deadline = Date.now() + EXPORT_WATCHDOG_MS;
    if (perfDiagnosticsEnabled()) blockPerfSink = (e) => blockPerf.push(e);
    perf.start("html2canvas:render");

    for (const entry of flatEntries) {
      if (opts.signal?.aborted) {
        status = "cancelled";
        throw new DOMException("Export cancelled", "AbortError");
      }
      if (Date.now() > deadline) {
        status = "failed:watchdog";
        throw new Error(
          `PDF export exceeded the ${Math.round(EXPORT_WATCHDOG_MS / 1000)}s limit while rendering "${
            entry.chapterTitle ?? entry.topBlockId ?? entry.el.id ?? entry.el.tagName
          }" (${renderedCount}/${totalEntries} blocks completed).`,
        );
      }
      try {
        const r = await renderBlock(
          entry.el,
          stage,
          contentWmm,
          contentHmm,
          entry.maxHmm,
          scaleOverride,
        );
        if (r) {
          r.breakBefore = entry.breakBefore;
          r.chapterTitle = entry.chapterTitle;
          r.keepWithPrev = entry.keepWithPrev;
          r.topBlockId = entry.topBlockId;
          rendered.push(r);
        }
      } catch (blockErr) {
        const info = {
          tag: entry.el.tagName,
          cls: (entry.el.className || "").toString().slice(0, 120),
          error:
            blockErr instanceof Error ? blockErr.message : String(blockErr),
        };
        renderFailures.push(info);
        console.error("[pdf] block render failed", info, blockErr);
      }
      renderedCount++;
      opts.onProgress?.({ phase: "rendering", rendered: renderedCount, total: totalEntries });
      // Yield to the browser between blocks so long exports don't freeze
      // the main thread (charts, evidence cards, and huge sub-block trees
      // otherwise dominate the frame budget on Chapter 03-sized articles).
      await new Promise((r) => setTimeout(r, 0));
    }
    perf.end("html2canvas:render");
    opts.onProgress?.({ phase: "assembling", rendered: renderedCount, total: totalEntries });
    if (rendered.length === 0) {
      throw new Error(
        `PDF renderer could not capture any report section (${renderFailures.length} block failure${renderFailures.length === 1 ? "" : "s"}). First error: ${renderFailures[0]?.error ?? "unknown"}`,
      );
    }

  } finally {
    hidden.forEach(({ el, prev }) => {
      el.style.display = prev;
    });
    // NOTE: releaseRoot() is intentionally NOT called here — the outer
    // `finally` at the end of generatePdfFromDom guarantees it runs
    // exactly once for every export attempt (success, failure, or throw).
  }

  if (renderFailures.length) {
    console.warn(
      `[pdf] ${renderFailures.length} block(s) skipped during export`,
      renderFailures,
    );
  }

  // Original invoice attachments become extra pages we need to include in the
  // page-count so header/footer "Page X of Y" is correct.
  const skipAttachments = sectionMode || opts.skipInvoiceAppendix === true;
  const attachments = skipAttachments ? [] : pickInvoiceAttachments(opts.invoiceFiles);
  const invoiceAttachmentIds = new Set(attachments.map((f) => f.id));
  const supportingPhotos = skipAttachments
    ? []
    : pickSupportingPhotoAttachments(opts.invoiceFiles, invoiceAttachmentIds);
  const pdfAttachments: { file: ReviewFile; bytes: Uint8Array; pageCount: number }[] = [];
  pdfAttachmentsRef = pdfAttachments;
  const imageAttachments: ImageAttachmentPage[] = [];
  // Every file we tried to embed but couldn't — used to render a professional
  // placeholder page instead of silently skipping Appendix A.
  const failedAttachments: { file: ReviewFile; reason: string }[] = [];

  // Files present in opts.invoiceFiles but not classifiable as pdf/image →
  // unsupported for embedding. Still surface them on the placeholder page.
  if (opts.invoiceFiles?.length) {
    for (const f of opts.invoiceFiles) {
      if (!isPdfFile(f) && !isImageFile(f)) {
        failedAttachments.push({
          file: f,
          reason: "Unsupported file type for PDF embedding",
        });
      }
    }
  }

  status = "failed:attachments-fetch";
  perf.start("attachments:fetch");
  // Kick off the pdf-lib import and every attachment fetch concurrently.
  // The two used to run serially, adding one signed-URL round trip per file.
  const pdfLibPromise: Promise<typeof import("pdf-lib") | null> = attachments.length
    ? import("pdf-lib").catch(() => null)
    : Promise.resolve(null);

  const invoiceBytesPromise = Promise.all(attachments.map(fetchBytes));
  const supportingBytesPromise = Promise.all(supportingPhotos.map(fetchBytes));

  const [PDFDocumentMod, invoiceBytesList, supportingBytesList] = await Promise.all([
    pdfLibPromise,
    invoiceBytesPromise,
    supportingBytesPromise,
  ]);
  perf.end("attachments:fetch");

  /**
   * Rasterize a raw image attachment into a jsPDF-ready JPEG data URL and
   * compute its printable dimensions. Extracted so the invoice-front-matter
   * loop and the supporting-photo (Appendix B) loop share one code path.
   */
  async function rasterizeImageAttachment(
    file: ReviewFile,
    bytes: Uint8Array,
    titleReserveMm: number,
  ): Promise<{
    dataUrl: string;
    wMm: number;
    hMm: number;
  } | null> {
    const blob = new Blob([bytes as BlobPart], {
      type: (file.mime_type ?? "image/jpeg").toLowerCase(),
    });
    const url = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = url;
      });
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      const ctx = c.getContext("2d");
      if (!ctx) return null;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0);
      const dataUrl = c.toDataURL("image/jpeg", PHOTO_JPEG_QUALITY);
      const availableHmm = contentHmm - titleReserveMm;
      const aspect = c.height / c.width;
      let wMm = contentWmm;
      let hMm = wMm * aspect;
      if (hMm > availableHmm) {
        const s = availableHmm / hMm;
        hMm = availableHmm;
        wMm = wMm * s;
      }
      return { dataUrl, wMm, hMm };
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // Preserve original ordering: iterate attachments in their input order so
  // Appendix A page sequence exactly matches the previous serial implementation.
  for (let i = 0; i < attachments.length; i++) {
    const file = attachments[i];
    const bytes = invoiceBytesList[i];
    if (!bytes) {
      failedAttachments.push({
        file,
        reason: "Signed URL failure or file missing in secure storage",
      });
      continue;
    }
    if (isPdfFile(file) && PDFDocumentMod) {
      try {
        const src = await PDFDocumentMod.PDFDocument.load(bytes, {
          ignoreEncryption: true,
        });
        pdfAttachments.push({
          file,
          bytes,
          pageCount: src.getPageCount(),
        });
      } catch {
        failedAttachments.push({
          file,
          reason: "Corrupt or encrypted PDF — could not be embedded",
        });
      }
    } else if (isPdfFile(file) && !PDFDocumentMod) {
      failedAttachments.push({
        file,
        reason: "PDF renderer unavailable in this browser session",
      });
    } else if (isImageFile(file)) {
      try {
        const raster = await rasterizeImageAttachment(file, bytes, 34);
        if (!raster) {
          failedAttachments.push({
            file,
            reason: "Browser canvas unavailable — could not render image",
          });
          continue;
        }
        imageAttachments.push({
          file,
          dataUrl: raster.dataUrl,
          wMm: raster.wMm,
          hMm: raster.hMm,
          appendixTitle: "Original Submitted Invoice",
          isInvoiceFrontMatter: true,
        });
      } catch {
        failedAttachments.push({
          file,
          reason: "Unreadable image file — could not be embedded",
        });
      }
    }
  }

  for (let i = 0; i < supportingPhotos.length; i++) {
    const file = supportingPhotos[i];
    const bytes = supportingBytesList[i];
    if (!bytes) continue;
    try {
      const raster = await rasterizeImageAttachment(file, bytes, 12);
      if (!raster) continue;
      imageAttachments.push({
        file,
        dataUrl: raster.dataUrl,
        wMm: raster.wMm,
        hMm: raster.hMm,
        appendixTitle: "Appendix B — Supporting Photo Evidence",
      });
    } catch {
      // Supporting photos should never block the report download. If a phone
      // image is in a browser-unsupported format, the file remains listed in
      // the appendix index and available from the dashboard.
    }
  }


  // ── Balance pass: reorder blocks *within a chapter* to eliminate large
  // whitespace tails. When a page would end with more than ~35% empty and
  // the next block belongs to the same chapter, we hoist a later block
  // from the same chapter that fits into the remaining space. Cards are
  // never split, chapter order is preserved, and welded (keepWithPrev)
  // blocks stay pinned to their predecessor.
  // Retained for signature compatibility; V1.1 always opens a chapter on a
  // fresh page, so no "does a section still fit under the banner" test runs.
  const MIN_SECTION_AFTER_BANNER_MM = contentHmm * 0.18;
  const balanced = reflowForBalance(
    rendered,
    contentTop,
    contentBottom,
    contentHmm,
    MIN_SECTION_AFTER_BANNER_MM,
  );

  // ── First pass: compute how many report-body pages we need ──

  let simPages = 1;
  let simY = contentTop;
  for (let i = 0; i < balanced.length; i++) {
    const b = balanced[i];
    const atPageTop = simY <= contentTop + 0.1;
    const fits = simY + b.hMm <= contentBottom + 0.1;
    if (b.breakBefore) {
      // Major chapter: always opens a fresh page.
      if (!atPageTop) {
        simPages++;
        simY = contentTop;
      }
    } else if (!atPageTop && !fits) {
      simPages++;
      simY = contentTop;
    }
    simY += b.hMm + GAP_MM;
  }

  const bodyPages = simPages;
  const imagePages = imageAttachments.length;
  const invoiceImagePages = imageAttachments.filter((p) =>
    invoiceAttachmentIds.has(p.file.id),
  ).length;
  const pdfPagesTotal = pdfAttachments.reduce((n, p) => n + p.pageCount, 0);
  // Emit a placeholder Appendix A page whenever we could not embed any
  // original invoice (missing file, corrupt/encrypted PDF, signed-URL failure,
  // unsupported type, or no invoice file at all). Never silently skip.
  const needPlaceholder = !skipAttachments && pdfPagesTotal === 0 && invoiceImagePages === 0;
  const placeholderPages = needPlaceholder ? 1 : 0;
  totalPages = bodyPages + imagePages + pdfPagesTotal + placeholderPages;


  // ── Second pass: emit body ──
  // Track the running chapter title so every emitted page's header shows
  // the correct section name, not just the first one.
  status = "failed:jspdf";
  perf.start("jspdf:assemble");
  let pageIndex = 0;
  let currentChapterTitle: string | undefined = opts.sectionTitle;
  drawChrome(pdf, pageWmm, pageHmm, pageIndex, totalPages, fileLabel, currentChapterTitle, versionLine, packageLabel);
  let y = contentTop;


  // Split the image attachments up front so the invoice pages are emitted
  // as front matter (right after the TOC) and any Appendix B supporting
  // photos stay at the end of the report.
  const invoiceImageAttachments = imageAttachments.filter(
    (p) => p.isInvoiceFrontMatter,
  );
  const supportingImageAttachments = imageAttachments.filter(
    (p) => !p.isInvoiceFrontMatter,
  );

  /**
   * Render the "Original Submitted Invoice" front-matter pages
   * (image-based invoices + placeholder). PDF-based invoice pages are
   * inserted at the same position by pdf-lib after the jsPDF document is
   * assembled — this function returns the pdf-lib insertion index for that
   * merge (0-based; corresponds to "insert BEFORE existing page at idx").
   */
  let placeholderRendered = false;
  let pdfInvoiceInsertionIndex: number | null = null;
  let frontMatterEmitted = false;
  const emitInvoiceFrontMatter = () => {
    if (frontMatterEmitted) return;
    frontMatterEmitted = true;
    invoiceImageAttachments.forEach((img, idx) => {
      pdf.addPage(pageSize, "portrait");
      pageIndex++;
      drawChrome(
        pdf,
        pageWmm,
        pageHmm,
        pageIndex,
        totalPages,
        fileLabel,
        "Original Submitted Invoice",
        versionLine,
        packageLabel,
      );
      pdf.setTextColor(20);
      pdf.setFontSize(15);
      pdf.text(img.appendixTitle, MARGIN_MM, contentTop + 2);
      let textY = contentTop + 9;
      if (idx === 0) {
        pdf.setTextColor(70);
        pdf.setFontSize(9.5);
        const intro =
          "The following invoice was submitted for independent analysis. All findings, market comparisons, legal research, and recommendations contained in this report are based on the document below.";
        const introLines = pdf.splitTextToSize(intro, contentWmm);
        pdf.text(introLines, MARGIN_MM, textY);
        textY += introLines.length * 4.3 + 2;
      }
      const totalInv = invoiceImageAttachments.length;
      const caption =
        totalInv > 1
          ? `${img.file.file_name ?? "Attached image"} — page ${idx + 1} of ${totalInv}`
          : (img.file.file_name ?? "Attached image");
      pdf.setTextColor(120);
      pdf.setFontSize(8);
      const capLines = pdf.splitTextToSize(caption, contentWmm);
      pdf.text(capLines, MARGIN_MM, textY);
      const imgTop = Math.max(textY + 4, contentTop + 30);
      const availH = contentBottom - imgTop;
      // Center the image in remaining space, preserving the pre-sized aspect.
      const drawH = Math.min(img.hMm, availH);
      const drawW = drawH === img.hMm ? img.wMm : img.wMm * (drawH / img.hMm);
      const x = MARGIN_MM + (contentWmm - drawW) / 2;
      const yy = imgTop + Math.max(0, (availH - drawH) / 2);
      pdf.addImage(img.dataUrl, "JPEG", x, yy, drawW, drawH, undefined, "SLOW");
    });
    if (needPlaceholder) {
      pdf.addPage(pageSize, "portrait");
      pageIndex++;
      drawChrome(
        pdf,
        pageWmm,
        pageHmm,
        pageIndex,
        totalPages,
        fileLabel,
        "Original Submitted Invoice",
        versionLine,
        packageLabel,
      );
      drawInvoicePlaceholderPage(
        pdf,
        pageWmm,
        contentTop,
        contentWmm,
        failedAttachments,
      );
      placeholderRendered = true;
    }
    // pdf-lib will insert the external PDF invoice pages at this position
    // (immediately after the last front-matter page we just emitted).
    pdfInvoiceInsertionIndex = pageIndex + 1;
  };

  for (let i = 0; i < balanced.length; i++) {
    const b = balanced[i];
    const atPageTop = y <= contentTop + 0.1;
    const fits = y + b.hMm <= contentBottom + 0.1;
    // Major chapter banners always open a fresh page (V1.1 layout rule).
    const forcedBreak = b.breakBefore && !atPageTop;
    const overflowBreak = !b.breakBefore && !atPageTop && !fits;
    const needsBreak = forcedBreak || overflowBreak;

    if (b.chapterTitle) currentChapterTitle = b.chapterTitle;
    if (needsBreak) {
      pdf.addPage(pageSize, "portrait");
      pageIndex++;
      drawChrome(pdf, pageWmm, pageHmm, pageIndex, totalPages, fileLabel, currentChapterTitle, versionLine, packageLabel);
      y = contentTop;
    } else if (atPageTop && b.chapterTitle) {
      // First block on this page carries a chapter title — repaint chrome
      // so the header reflects the new section.
      drawChrome(pdf, pageWmm, pageHmm, pageIndex, totalPages, fileLabel, currentChapterTitle, versionLine, packageLabel);
    }
    const x = MARGIN_MM + (contentWmm - b.wMm) / 2;
    // PNG deflate is lossless either way; "FAST" only lowers the zlib effort
    // level, so pixels are byte-identical while jsPDF stops spending the bulk
    // of the export budget re-compressing every text raster (SAB-038).
    // Photographic blocks arrive pre-encoded as JPEG (adaptive raster path).
    pdf.addImage(b.dataUrl, b.format ?? "PNG", x, y, b.wMm, b.hMm, undefined, "FAST");


    y += b.hMm + GAP_MM;

    // As soon as we finish emitting every sub-block of the "package-contents"
    // (Table of Contents) top block, inject the Original Invoice front matter
    // before the report body continues with the Executive chapter.
    if (
      !frontMatterEmitted &&
      !skipAttachments &&
      b.topBlockId === "package-contents" &&
      (i + 1 >= balanced.length || balanced[i + 1].topBlockId !== "package-contents")
    ) {
      emitInvoiceFrontMatter();
      // The next body block should start on a fresh page.
      y = contentBottom + 1;
    }
  }

  // Fallback: if we never saw the TOC block (e.g. it was excluded), emit
  // the invoice front matter at the end so it is never silently dropped.
  if (!skipAttachments && !frontMatterEmitted) {
    emitInvoiceFrontMatter();
  }

  // ── Supporting photo attachments (Appendix B): one image per page ──
  for (const img of supportingImageAttachments) {
    pdf.addPage(pageSize, "portrait");
    pageIndex++;
    drawChrome(pdf, pageWmm, pageHmm, pageIndex, totalPages, fileLabel, opts.sectionTitle, versionLine, packageLabel);
    pdf.setTextColor(30);
    pdf.setFontSize(11);
    pdf.text(img.appendixTitle, MARGIN_MM, contentTop);
    pdf.setTextColor(100);
    pdf.setFontSize(8);
    const fileCaption = pdf.splitTextToSize(img.file.file_name ?? "Attached image", contentWmm);
    pdf.text(fileCaption, MARGIN_MM, contentTop + 5);
    const x = MARGIN_MM + (contentWmm - img.wMm) / 2;
    const yy = contentTop + 12 + (contentHmm - 12 - img.hMm) / 2;
    pdf.addImage(img.dataUrl, "JPEG", x, yy, img.wMm, img.hMm, undefined, "SLOW");
  }
  perf.end("jspdf:assemble");

  const filename = opts.filenameBase
    ? `${opts.filenameBase}.pdf`
    : buildFilename(opts.fileNumber || opts.invoiceNumber, opts.packageType);

  // ── Merge original PDF invoice pages via pdf-lib as Appendix A ──
  let mergedSaved = false;
  let mergedInvoicePdfs = 0;
  if (pdfAttachments.length && PDFDocumentMod) {
    try {
      status = "failed:pdf-lib";
      perf.start("pdflib:merge");
      const reportBytes = pdf.output("arraybuffer");
      const merged = await PDFDocumentMod.PDFDocument.load(reportBytes);
      // Insert each invoice PDF's pages at the front-matter position (right
      // after the TOC) so the Original Invoice appears BEFORE the Executive
      // chapter. Falls back to appending when no insertion point is known.
      let insertCursor = pdfInvoiceInsertionIndex;
      for (const a of pdfAttachments) {
        try {
          const src = await PDFDocumentMod.PDFDocument.load(a.bytes, {
            ignoreEncryption: true,
          });
          const copied = await merged.copyPages(src, src.getPageIndices());
          for (const p of copied) {
            if (insertCursor !== null) {
              merged.insertPage(insertCursor, p);
              insertCursor++;
            } else {
              merged.addPage(p);
            }
          }
        } catch {
          // skip
        }
      }


      if (reportMeta) {
        try {
          merged.setTitle(
            `${identity.titlePlain}${opts.fileNumber ? ` — File ${opts.fileNumber}` : ""}`,
          );
          merged.setSubject(`${identity.title} v${reportMeta.package_version}`);
          merged.setAuthor("ClaimGuard Professional Review Services");
          merged.setCreator(
            `ClaimGuard Intelligence Engine v${reportMeta.intelligence_engine_version}`,
          );
          merged.setProducer(`ClaimGuard PDF Engine · ${reportMeta.report_uuid}`);
          merged.setKeywords([
            `package:${identity.key}`,
            `report_uuid:${reportMeta.report_uuid}`,
            `package_version:${reportMeta.package_version}`,
            `intelligence_engine:${reportMeta.intelligence_engine_version}`,
            `legal_dataset:${reportMeta.legal_dataset_version}`,
            `market_dataset:${reportMeta.market_dataset_version}`,
            `ocr_engine:${reportMeta.ocr_engine}`,
            `confidence_engine:${reportMeta.confidence_engine_version}`,
            `generated_at:${reportMeta.generated_at}`,
          ]);
        } catch {
          // non-fatal
        }
      }
      const outBytes = await merged.save();
      perf.end("pdflib:merge");
      status = "failed:save";
      perf.start("save:blob");
      const blob = new Blob([outBytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      perf.end("save:blob");
      resultBytes = outBytes.byteLength;
      mergedSaved = true;
      mergedInvoicePdfs = pdfAttachments.length;
    } catch {
      // fall through to plain save
    }
  }

  if (!mergedSaved) {
    status = "failed:save";
    perf.start("save:blob");
    pdf.save(filename);
    perf.end("save:blob");
    // Approximate byte count for the summary (pdf.output is O(pdf-size); this
    // is cheap even for large exports).
    try {
      resultBytes = (pdf.output("arraybuffer") as ArrayBuffer).byteLength;
    } catch {
      /* ignore */
    }
  }

  status = "success";
  return {
    mergedInvoicePdfs,
    renderedInvoiceImages: invoiceImagePages,
    placeholderRendered,
  };
  } catch (err) {
    if (status === "in-progress" || status === "success") {
      status = "failed:unknown";
    }
    throw err;
  } finally {
    blockPerfSink = null;
    try {
      stage?.destroy();

    } catch {
      /* never let cleanup throw */
    }
    stage = null;
    try {
      releaseRoot();
    } catch {
      /* never let cleanup throw */
    }
    releaseRoot = () => undefined;
    emitPerfSummary();
  }

}



/* ─────────────────────── placeholder page ─────────────────────── */

function formatUploadDate(iso: string | null | undefined): string {
  if (!iso) return "Unknown";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function drawInvoicePlaceholderPage(
  pdf: import("jspdf").jsPDF,
  pageWmm: number,
  contentTop: number,
  contentWmm: number,
  failed: { file: ReviewFile; reason: string }[],
) {
  const left = MARGIN_MM;
  let y = contentTop + 8;

  // Title
  pdf.setTextColor(20);
  pdf.setFontSize(20);
  pdf.text("Appendix A", left, y);
  y += 8;
  pdf.setTextColor(80);
  pdf.setFontSize(13);
  pdf.text("Original Submitted Invoice", left, y);
  y += 10;

  pdf.setDrawColor(220);
  pdf.setLineWidth(0.3);
  pdf.line(left, y, pageWmm - left, y);
  y += 8;

  // Body statement
  pdf.setTextColor(40);
  pdf.setFontSize(11);
  const body =
    "The original invoice could not be embedded in this PDF package.";
  pdf.text(pdf.splitTextToSize(body, contentWmm), left, y);
  y += 8;

  pdf.setTextColor(90);
  pdf.setFontSize(10);
  const detail =
    "The original file remains securely stored in the case file and can be accessed from the web portal if available. Contact support if you need a direct copy.";
  const detailLines = pdf.splitTextToSize(detail, contentWmm);
  pdf.text(detailLines, left, y);
  y += detailLines.length * 5 + 6;

  // File metadata table (if any attempts were tracked)
  if (failed.length) {
    pdf.setTextColor(30);
    pdf.setFontSize(11);
    pdf.text("Files attempted", left, y);
    y += 6;
    pdf.setDrawColor(230);
    pdf.line(left, y, pageWmm - left, y);
    y += 4;

    pdf.setFontSize(9);
    for (const item of failed) {
      const f = item.file;
      pdf.setTextColor(30);
      pdf.text(`File name: ${f.file_name ?? "Unknown"}`, left, y);
      y += 4.5;
      pdf.setTextColor(80);
      pdf.text(
        `Type: ${f.mime_type ?? "Unknown"}    Uploaded: ${formatUploadDate(f.created_at)}`,
        left,
        y,
      );
      y += 4.5;
      pdf.text("Secure storage status: Retained in case file", left, y);
      y += 4.5;
      pdf.setTextColor(150, 40, 40);
      const reasonLines = pdf.splitTextToSize(
        `Reason not embedded: ${item.reason}`,
        contentWmm,
      );
      pdf.text(reasonLines, left, y);
      y += reasonLines.length * 4.5 + 3;
      pdf.setDrawColor(240);
      pdf.line(left, y, pageWmm - left, y);
      y += 4;
      if (y > 250) break; // safety, stay on one page
    }
  } else {
    pdf.setTextColor(90);
    pdf.setFontSize(10);
    const none = pdf.splitTextToSize(
      "No original invoice file was available at export time. The case may have been submitted by email or another channel where the invoice is retained separately in the secure case record.",
      contentWmm,
    );
    pdf.text(none, left, y);
  }
}


/* ─────────────────────── hook ─────────────────────── */

// `ReportPackage` type re-exported at the top of this module from
// `@/lib/report/package-identity` (single source of truth).

/**
 * Chapter anchors per download package. `complete` returns undefined so the
 * PDF engine falls back to including every chapter (the current behavior).
 * `review` omits the negotiation playbook; `negotiation` keeps only the
 * executive dashboard and negotiation strategy.
 */
export function chaptersForPackage(pkg: ReportPackage): string[] | undefined {
  if (pkg === "complete") return undefined;
  // Registry-driven. Reading order flows through here automatically so
  // reordering CHAPTERS updates every package export in the same commit.
  const executive = getChapterByInternalId("executive-decision").anchorId;
  const negotiation = getChapterByInternalId("negotiation-strategy").anchorId;
  const conclusion = getChapterByInternalId("conclusion").anchorId;
  if (pkg === "review") {
    // Professional Review Report: investigative/analytical chapters only.
    // Excludes Executive Decision Cheat Sheet, Negotiation Strategy, and
    // Professional Review Conclusion.
    const excluded = new Set([executive, negotiation, conclusion]);
    return CHAPTERS.map((c) => c.anchorId).filter((id) => !excluded.has(id));
  }
  // Negotiation Package: Executive Decision Cheat Sheet + Negotiation
  // Strategy + Professional Review Conclusion.
  return [executive, negotiation, conclusion];
}




// Package labels/filename suffixes are derived from PACKAGE_IDENTITY.




export function useReportPdfActions(
  reviewId: string | undefined | null,
  invoiceNumber?: string,
  fileNumber?: string,
  invoiceFiles?: ReviewFile[],
) {
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  function cancelDownload() {
    abortRef.current?.abort();
  }

  async function downloadOrGenerate(pkg: ReportPackage = "complete") {
    if (busy) return;
    setBusy(true);
    const identity = getPackageIdentity(pkg);
    const label = identity.title;
    const controller = new AbortController();
    abortRef.current = controller;
    const toastId = toast.loading(`Preparing your ${label}…`, {
      action: { label: "Cancel", onClick: () => controller.abort() },
    });
    try {
      // Exported files are named with the ClaimGuard file number (INV-YYYY-…),
      // falling back to the vendor invoice number only when absent.
      const idPart =
        sanitizeForFilename(fileNumber) ||
        sanitizeForFilename(invoiceNumber) ||
        "REPORT";
      const filenameBase = `ClaimGuard-${identity.filenameSlug}-${idPart}`;
      const result = await generatePdfFromDom({
        invoiceNumber,
        fileNumber,
        pageSize: "a4",
        invoiceFiles: pkg === "negotiation" ? undefined : invoiceFiles,
        skipInvoiceAppendix: pkg === "negotiation",
        includeAnchors: chaptersForPackage(pkg),
        packageType: pkg,
        filenameBase,
        signal: controller.signal,
        onProgress: (info) => {
          const message =
            info.phase === "preparing"
              ? `Preparing your ${label}…`
              : info.phase === "rendering"
                ? `Building your ${label} — section ${info.rendered} of ${info.total}`
                : info.phase === "assembling"
                  ? `Assembling your ${label}…`
                  : `Finalizing your ${label}…`;
          toast.loading(message, {
            id: toastId,
            action: { label: "Cancel", onClick: () => controller.abort() },
          });
        },
      });
      void trackEvent({
        event: "report_pdf_downloaded",
        properties: {
          review_id: reviewId ?? null,
          package: pkg,
          merged_invoice_pdfs: result.mergedInvoicePdfs,
          rendered_invoice_images: result.renderedInvoiceImages,
          appendix_placeholder: result.placeholderRendered,
        },
      });
      const includedInvoice =
        result.mergedInvoicePdfs > 0 || result.renderedInvoiceImages > 0;
      toast.success(
        includedInvoice
          ? `${label} downloaded — original invoice included as Appendix A.`
          : `${label} downloaded successfully`,
        { id: toastId, action: undefined },
      );
    } catch (err) {
      if (controller.signal.aborted) {
        toast.message("Download cancelled.", { id: toastId, action: undefined });
        return;
      }
      const message = err instanceof Error ? err.message : String(err);
      console.error("[pdf] downloadOrGenerate failed", err);
      captureException(err, {
        source: "pdf",
        location: "use-report-pdf.downloadOrGenerate",
      });
      toast.error(`We couldn't generate your PDF: ${message}`, {
        id: toastId,
        duration: 10000,
        action: undefined,
      });
    } finally {
      abortRef.current = null;
      setBusy(false);
    }
  }

  return { downloadOrGenerate, cancelDownload, busy };
}


/**
 * Per-section export: download a single section as a standalone PDF with
 * ClaimGuard branding, section title in the header, and page numbers. The
 * caller passes the section's DOM id and title.
 */
export function useSectionPdfActions(fileNumber?: string) {
  const [busyId, setBusyId] = useState<string | null>(null);

  async function downloadSection(
    sectionId: string,
    sectionTitle: string,
    opts?: {
      kind?: "chapter" | "section";
      chapterNumber?: string;
      chapter?: import("@/lib/report/chapter-registry").ChapterEntry;
    },
  ) {
    if (busyId) return;
    if (typeof document === "undefined") return;
    const { withScopedExport } = await import("@/lib/report/export-mode");
    const el = document.getElementById(sectionId);
    if (!el) {
      toast.error("Could not find that section to export.");
      return;
    }
    const kind = opts?.kind ?? "section";
    const scopeLabel = kind === "chapter" ? "chapter" : "section";
    setBusyId(sectionId);
    const toastId = toast.loading(`Preparing ${scopeLabel} PDF — ${sectionTitle}…`);
    try {
      const entry = opts?.chapter;
      const filenames = await import("@/lib/report/filenames");
      const filenameBase =
        kind === "chapter"
          ? // Chapter exports are named after the chapter itself:
            // ClaimGuard_<Chapter_Name>.pdf
            filenames.buildChapterOnlyPdfFilenameBase(entry?.title ?? sectionTitle)
          : entry
            ? filenames.buildSectionPdfFilenameFromEntry(fileNumber, entry, sectionTitle)
            : buildSectionPdfFilenameBase(fileNumber, opts?.chapterNumber, sectionTitle);


      // Scoped exports capture the live DOM: force every collapsed chapter
      // control (e.g. the Market Evidence Review focus mode) to render its
      // complete content first, so the export can never be a partial view.
      await withScopedExport(() =>
        generatePdfFromDom({
          pageSize: "a4",
          fileNumber,
          rootElement: el,
          skipInvoiceAppendix: true,
          sectionTitle,
          filenameBase,
          onProgress: (info) => {
            if (info.total > 1) {
              toast.loading(
                `Preparing ${scopeLabel} PDF — ${sectionTitle} (${info.rendered}/${info.total})`,
                { id: toastId },
              );
            }
          },
        }),
      );
      toast.success(`“${sectionTitle}” downloaded.`, { id: toastId });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error("[pdf] section download failed", err);
      captureException(err, {
        source: "pdf",
        location: "use-report-pdf.downloadSection",
      });
      toast.error(`Couldn't export ${scopeLabel}: ${message}`, {
        id: toastId,
        duration: 8000,
      });
    } finally {
      setBusyId(null);
    }
  }

  function printSection(
    sectionId: string,
    sectionTitle: string,
    opts?: { kind?: "chapter" | "section" },
  ) {
    if (typeof document === "undefined") return;
    const el = document.getElementById(sectionId);
    if (!el) {
      toast.error("Could not find that section to print.");
      return;
    }
    // Hide every element outside the target's ancestor chain, print, restore.
    const restore: { el: HTMLElement; prev: string }[] = [];
    const walk = (node: HTMLElement) => {
      let cur: HTMLElement | null = node;
      while (cur && cur.parentElement && cur !== document.body) {
        const parent: HTMLElement = cur.parentElement;
        for (const sib of Array.from(parent.children) as HTMLElement[]) {
          if (sib !== cur && !sib.contains(node)) {
            restore.push({ el: sib, prev: sib.style.display });
            sib.style.display = "none";
          }
        }
        cur = parent;
      }
    };
    walk(el);
    // Expand every collapsed chapter control so a scoped print is complete.
    setScopedExportActive(true);
    // Marks the printed subtree so print CSS can suppress the forced
    // chapter page-break (otherwise a scoped print leads with a blank page).
    el.setAttribute("data-print-scope", "true");
    document.documentElement.setAttribute("data-print-scoped", "true");
    const prevTitle = document.title;
    // Browsers use document.title as the suggested "Save as PDF" filename.
    document.title =
      opts?.kind === "chapter"
        ? buildChapterOnlyPdfFilenameBase(sectionTitle)
        : `ClaimGuard — ${sectionTitle}`;
    const cleanup = () => {
      restore.forEach(({ el: e, prev }) => {
        e.style.display = prev;
      });
      el.removeAttribute("data-print-scope");
      document.documentElement.removeAttribute("data-print-scoped");
      document.title = prevTitle;
      setScopedExportActive(false);
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    // Fallback in case afterprint doesn't fire (some browsers).
    setTimeout(cleanup, 4000);
    // Give React one frame to render the expanded content before printing.
    setTimeout(() => window.print(), 50);
  }


  return { downloadSection, printSection, busyId };
}
